import React, { MouseEventHandler, useEffect, useState, useCallback } from 'react'
import type { TableColumnsType } from 'antd'
import { Button, Input, Select, Space, Table, Tooltip, Typography } from 'antd'
import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import { useFiltersV2 } from '../../contexts/FiltersContextV2'
import useSuppliersView from '../../hooks/useSuppliersView'
import { AssignmentZone, Depot, Supplier } from '../../types'
import { StyledButton, StyledRow, StyledSelect } from '../StyledComponents'
import { MapsModal } from '../MapsModal/MapsModal'
import { AddDepotModal } from './AddDepotModal/AddDepotModal'
import { useUser } from '../../contexts'
import { AddSupplierModal } from '../../features/TplRouter/TplDashboard/components/AddSupplierModal'
import { EditSupplierModal } from '../../features/TplRouter/TplDashboard/components/EditSupplierModal'
import { EditDepotModal } from './EditDepotModal/EditDepotModal'

const { Option } = Select

enum SupplierAction {
  EDIT_SUPPLIER = 'EDIT_SUPPLIER',
  ADD_DEPOT = 'ADD_DEPOT',
  EDIT_DEPOT = 'EDIT_DEPOT',
}

const SupplierView: React.FC = () => {
  const { filters, handleResetFilters, handleFilterChange, resetFilters } = useFiltersV2()
  const { suppliers, isLoading } = useSuppliersView({ filters })
  const { t } = useTranslation()
  const { state: user } = useUser()
  const [expandedRowKey, setExpandedRowKey] = useState<string | number | null>(null)

  const [isMapsModalOpen, setIsMapsModalOpen] = useState(false)
  const [locationLatitude, setLocationLatitude] = useState<number | undefined>(undefined)
  const [locationLongitude, setLocationLongitude] = useState<number | undefined>(undefined)
  const [locationStreet, setLocationStreet] = useState<string[] | string | undefined>(undefined)
  const [locationCity, setLocationCity] = useState<string[] | string | undefined>(undefined)
  const [locationState, setLocationState] = useState<string[] | string | undefined>(undefined)
  const [locationNumber, setLocationNumber] = useState<string[] | string | undefined>(undefined)
  const [tag, setTag] = useState<string | undefined>(undefined)
  const [partialNameValue, setPartialNameValue] = useState<string>()
  const [cityValue, setCityValue] = useState<string>()
  const [selectedSupplier, setSelectedSupplier] = useState<number>()
  const [isAddSupplierModalOpen, setisAddSupplierModalOpen] = useState(false)
  const [supplierAction, setSupplierAction] = useState<SupplierAction>()
  const [selectedDepot, setSelectedDepot] = useState<Depot>()
  const handleCloseAddSupplierModal = () => {
    setisAddSupplierModalOpen(false)
  }

  const handleOpenMapsModal = (depot: Depot) => {
    setIsMapsModalOpen(true)
    if (depot.location?.coordinates) {
      setLocationLatitude(depot.location.coordinates[0])
      setLocationLongitude(depot.location.coordinates[1])
      setLocationStreet(depot.street || '')
      setLocationNumber(depot.number || '')
      setLocationState(depot.state || '')
      setLocationCity(depot.city || '')
      setTag(depot.tag || '')
    }
  }

  const expandedRowRender = (record: Supplier) => {
    const filteredDepots = cityValue
      ? record.depots.filter(depot => depot.city.toLowerCase().includes(cityValue.toLowerCase()))
      : record.depots

    const columnsDepotDate: TableColumnsType<Depot> = [
      { title: t('data_address.depot'), dataIndex: 'tag', key: 'tag' },
      { title: t('data_address.street'), dataIndex: 'street', key: 'street' },
      { title: t('data_address.number'), dataIndex: 'number', key: 'number' },
      { title: t('data_address.city'), dataIndex: 'city', key: 'city' },
      { title: t('data_address.code'), dataIndex: ['sapGeo', 'cityCode'], key: 'sapGeo.cityCode' },
      { title: t('data_address.state'), dataIndex: 'state', key: 'state' },

      {
        title: t('data_address.scrap_zone'),
        dataIndex: 'assignmentZone',
        key: 'assignmentZone',
        render: (assignmentZone: AssignmentZone | null) => {
          if (assignmentZone && assignmentZone.name && assignmentZone.code) {
            return `${assignmentZone.name} (${assignmentZone.code})`
          } else {
            return '-'
          }
        },
      },
      {
        title: t('data_address.actions'),
        key: 'action',
        align: 'center',
        render: (_: any, depot: Depot) => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Space size="middle">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleOpenMapsModal(depot)}
              />
              {user?.roles[0].name === 'ADMIN' && (
                <Tooltip title={t('depot_modal.edit_depot')}>
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={handleOpenEditDepotModal(depot, record)}
                  />
                </Tooltip>
              )}
            </Space>
          </div>
        ),
      },
    ]

    return <Table columns={columnsDepotDate} dataSource={filteredDepots} pagination={false} />
  }

  const columnsSuppliers: TableColumnsType<Supplier> = [
    { title: t('data_address.supplier'), dataIndex: 'tradeName', key: 'supplier' },
    { title: t('data_address.email'), dataIndex: 'email', key: 'email' },
    { title: t('data_address.cellphone'), dataIndex: 'cellphone', key: 'cellphone' },
    { title: t('data_address.city'), dataIndex: 'city', key: 'city' },
    { title: t('data_address.sap_code'), dataIndex: 'sapId', key: 'sapId' },
    {
      title: t('tpl_dashboard.status'),
      dataIndex: 'disableAt',
      key: 'disableAt',
      render: (disableAt: Date) =>
        disableAt === null ? (
          <span>{t('globals.generics.enabled')}</span>
        ) : (
          <span style={{ color: 'red' }}>{t('globals.generics.disabled')}</span>
        ),
    },
    {
      title: (
        <Typography.Text style={{ display: 'block', textAlign: 'center' }}>
          Acciones
        </Typography.Text>
      ),
      key: 'action',
      dataIndex: 'id',
      render: supplierId => (
        <Space
          size="middle"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginLeft: '20px',
            height: '100%',
          }}
        >
          {user?.roles[0].name === 'ADMIN' && (
            <Tooltip title={t('add_depot')}>
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={handleOpenAddDepotModal(supplierId)}
              />
            </Tooltip>
          )}
          {user?.roles[0].name === 'ADMIN' && (
            <Tooltip title={t('edit_supplier')}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={handleOpenEditSupplierModal(supplierId)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]

  const handleSearchText = (value?: string | null | undefined) => {
    if (value === '' || !value) {
      handleFilterChange('partialName')(undefined)
    } else {
      handleFilterChange('partialName')(value)
    }
  }

  const setupRowClassName = (_record: any, index: number) =>
    `expandable-row ${index % 2 === 0 ? 'odd-row' : 'even-row'}`

  const handleExpand = (expanded: boolean, record: Supplier) => {
    if (!expanded) {
      setCityValue(undefined)
    }
    setExpandedRowKey(expanded ? record.id : null)
  }

  const handleCleanFilters = () => {
    handleResetFilters()
  }

  const handleCloseMapsModal = () => {
    setIsMapsModalOpen(false)
  }

  const resetViewFilters = useCallback(() => {
    setPartialNameValue(undefined)
    setCityValue(undefined)
  }, [])

  useEffect(() => {
    if (!!resetFilters) {
      resetViewFilters()
    }
  }, [resetFilters, resetViewFilters])

  const handleOpenAddDepotModal =
    (supplierId: number): MouseEventHandler<HTMLButtonElement> =>
    e => {
      e.stopPropagation()
      setSelectedSupplier(supplierId)
      setSupplierAction(SupplierAction.ADD_DEPOT)
    }

  const handleCloseModal = () => {
    setSelectedSupplier(undefined)
    setSelectedDepot(undefined)
    setSupplierAction(undefined)
  }
  const handleOpenEditSupplierModal =
    (supplierId: number): MouseEventHandler<HTMLButtonElement> =>
    e => {
      e.stopPropagation()
      setSelectedDepot(undefined)
      setSelectedSupplier(supplierId)
      setSupplierAction(SupplierAction.EDIT_SUPPLIER)
    }

  const handleOpenEditDepotModal =
    (depot: Depot, supplier: Supplier): MouseEventHandler<HTMLButtonElement> =>
    e => {
      e.stopPropagation()
      setSelectedSupplier(supplier.id)
      setSelectedDepot(depot)
      setSupplierAction(SupplierAction.EDIT_DEPOT)
    }

  return (
    <>
      <StyledRow className="sticky-options border-botom">
        <div
          className="filters-container"
          style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
        >
          <div
            className="filters-list"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1rem',
              marginLeft: '1rem',
            }}
          >
            <StyledButton className="inline option" onClick={handleCleanFilters}>
              {t('globals.generics.clean_filter')}
            </StyledButton>
            <Tooltip
              placement="bottomRight"
              title={
                user?.roles[0].name === 'ADMIN' || user?.roles[0].name === 'PROCUREMENT'
                  ? t('supplier_modal.new_supplier')
                  : ''
              }
            >
              <Button
                className="inline"
                type="primary"
                onClick={() => setisAddSupplierModalOpen(true)}
                style={{ display: user?.roles[0].name === 'ADMIN' ? 'inline-block' : 'none' }}
                disabled={!(user?.roles[0].name === 'ADMIN')}
              >
                <p style={{ marginTop: 1, marginLeft: 1 }}>{t('tpl_dashboard.new_supplier')}</p>
              </Button>
            </Tooltip>
            <Input
              style={{ width: '25%', minWidth: '20rem' }}
              placeholder={t('tpl_dashboard.search_name')}
              type="string"
              value={partialNameValue}
              onPressEnter={e => {
                handleSearchText(e.currentTarget.value)
              }}
              onKeyUp={e => {
                setPartialNameValue(e.currentTarget.value)
              }}
              onBlur={e => {
                handleSearchText(e.currentTarget.value)
              }}
              onChange={e => {
                setPartialNameValue(e.currentTarget.value)
                if (e.currentTarget.value === '' || !e.currentTarget.value) {
                  handleFilterChange('partialName')(undefined)
                }
              }}
              allowClear
            />
            <StyledSelect
              maxTagCount="responsive"
              allowClear
              value={filters.disabled}
              placeholder={t('tpl_dashboard.state')}
              onChange={handleFilterChange('disabled')}
            >
              <Option value={false}>{t('globals.generics.enabled')}</Option>
              <Option value={true}>{t('globals.generics.disabled')}</Option>
            </StyledSelect>
            {expandedRowKey && (
              <Input
                style={{ width: '25%', minWidth: '20rem' }}
                placeholder={t('tpl_dashboard.search_city')}
                type="string"
                value={cityValue}
                onChange={e => {
                  setCityValue(e.currentTarget.value)
                }}
                allowClear
              />
            )}
          </div>
          <Table
            columns={columnsSuppliers}
            loading={isLoading}
            expandable={{
              expandedRowRender,
              expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
              onExpand: handleExpand,
            }}
            dataSource={suppliers?.data}
            rowClassName={setupRowClassName}
            rowKey="id"
            style={{ width: '100%' }}
            pagination={{
              pageSize: filters.limit as number,
              onChange: handleFilterChange('page', false),
              total: suppliers?.total,
              showSizeChanger: false,
              current: filters.page as number,
            }}
            onRow={record => ({
              onClick: () => {
                const isExpanded = expandedRowKey === record.id
                setExpandedRowKey(isExpanded ? null : record.id)
              },
            })}
          />
        </div>
      </StyledRow>
      <MapsModal
        open={isMapsModalOpen}
        longitude={locationLongitude}
        latitude={locationLatitude}
        street={locationStreet}
        number={locationNumber}
        province={locationState}
        city={locationCity}
        close={handleCloseMapsModal}
        tag={tag}
      />
      <AddSupplierModal open={isAddSupplierModalOpen} onClose={handleCloseAddSupplierModal} />
      {selectedSupplier && supplierAction === SupplierAction.ADD_DEPOT && (
        <AddDepotModal
          selectedSupplier={selectedSupplier}
          key={selectedSupplier}
          onClose={handleCloseModal}
        />
      )}
      {selectedDepot && selectedSupplier && supplierAction === SupplierAction.EDIT_DEPOT && (
        <EditDepotModal
          selectedDepot={selectedDepot}
          key={selectedDepot.id}
          onClose={handleCloseModal}
        />
      )}

      {suppliers?.data && selectedSupplier && supplierAction === SupplierAction.EDIT_SUPPLIER && (
        <EditSupplierModal
          onClose={handleCloseModal}
          selectedSupplier={selectedSupplier}
          suppliersData={suppliers.data}
        />
      )}
    </>
  )
}

export default SupplierView
